<template>
    <div ref="root" class="px-6 pb-6 space-y-6">
        <!-- app-api refuses to delete an account that owns a license the member bought, and support
             closes it instead. Said before anything the deletion asks, so the member never confirms
             it, or answers for the people their plan protects, only to be refused. -->
        <template v-if="ownsPurchasedLicense">
            <p id="delete-account-owns-license" class="delete-refusal" data-testid="delete-account-owns-license">
                An account with a license you bought can't be deleted. Contact support to close it.
            </p>
            <div class="flex justify-end gap-3 pt-4">
                <PrimeButton label="Close" variant="secondary" data-testid="delete-account-close" @click="$emit('closeDialog')" />
                <PrimeButton
                    label="Contact support"
                    variant="primary"
                    autofocus
                    aria-describedby="delete-account-owns-license"
                    data-testid="delete-account-contact-support"
                    @click="closeForSupport"
                />
            </div>
        </template>

        <template v-else>
            <!-- What deleting removes, said at full ink on the modal's own ground: the modal states what
                 confirm does, and no zone is red (DESIGN.md). -->
            <div class="delete-warning">
                <h3 class="delete-warning__title">This can't be undone</h3>
                <p class="delete-warning__text">Deleting your account will permanently remove all your data, including:</p>
                <ul class="delete-warning__list">
                    <li>Personal information and settings</li>
                    <li>Exposure reports and scan history</li>
                    <li>Privacy preferences and opt-outs</li>
                    <li>Account credentials and access</li>
                </ul>
                <p class="delete-warning__text delete-warning__text--strong">
                    Once your account is deleted, you will lose access to all Privacy Bee services and your data cannot be recovered.
                </p>
            </div>

            <!-- Deleting the account ends the plan other people's protection rides on, so they are named
                 before the member confirms. While the read of who they are is out, their place is held
                 and Delete account waits, so the names cannot arrive after the member has gone ahead.
                 The status region mounts empty, since one that appears already holding its text is not
                 reliably announced, and says the read is out once it has mounted. -->
            <span class="sr-only" role="status">{{ readingStatus }}</span>
            <!-- A failed read is said in their place, with the way to read again, and holds Delete
                 account: the people come first. Try again says the failure, and hands focus to the
                 people it brings, or to the confirmation when it brings none. -->
            <section v-if="dependentsUnread" class="delete-dependents delete-dependents--unread" data-testid="delete-dependents-unread">
                <p class="delete-dependents__title">{{ DEPENDENTS_UNREAD }}</p>
                <RetryButton
                    :busy="readingDependents"
                    :failure="DEPENDENTS_UNREAD"
                    :focus-target="nextStep"
                    data-testid="delete-dependents-retry"
                    @retry="readDependents"
                />
            </section>
            <div v-else-if="readingDependents" class="delete-dependents" aria-hidden="true" data-testid="delete-dependents-loading">
                <PrimeSkeleton width="60%" height="var(--leading-sm)" />
                <PrimeSkeleton height="4rem" />
            </div>
            <section v-else-if="dependents.length > 0" class="delete-dependents" data-testid="delete-dependents">
                <h3 ref="peopleTitle" class="delete-dependents__title" tabindex="-1" data-testid="delete-dependents-title">
                    {{ revokeQuestion(dependents) }}
                </h3>
                <GiftDependentsList :dependents="dependents" />
                <p class="pb-supporting delete-dependents__loss">{{ DEPENDENTS_LOSE }}</p>
            </section>

            <ModalField
                class="mt-2 w-full"
                label="To confirm deletion, type DELETE in the field below."
                input-id="delete-confirmation"
            >
                <PrimeInputText
                    id="delete-confirmation"
                    v-model="deleteConfirmation"
                    placeholder="DELETE"
                    :invalid="deleteConfirmation !== '' && deleteConfirmation !== 'DELETE'"
                    :disabled="isDeleting"
                    class="w-full"
                />
            </ModalField>

            <!-- While the deletion is out, Delete account stays focusable and busy, so a refusal finds
                 the member where they pressed; Cancel and the field wait beside it. -->
            <div class="flex justify-end gap-3 pt-4">
                <PrimeButton label="Cancel" variant="secondary" autofocus @click="$emit('closeDialog')" :disabled="isDeleting" />
                <PrimeButton
                    label="Delete account"
                    variant="danger"
                    @click="confirmDelete"
                    :disabled="deleteConfirmation !== 'DELETE' || !dependentsSettled"
                    :aria-disabled="isDeleting ? 'true' : undefined"
                    :data-busy="isDeleting || readingDependents ? '' : undefined"
                    data-testid="delete-account-confirm"
                >
                    <template #icon>
                        <PbScanMark v-if="isDeleting || readingDependents" />
                    </template>
                </PrimeButton>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import GiftDependentsList from 'pb-billing-ui/GiftDependentsList.vue';
import { DEPENDENTS_LOSE, DEPENDENTS_UNREAD, revokeQuestion, type GiftDependent } from 'pb-billing-ui/gift-dependents.ts';
import { computed, onMounted, ref } from 'vue';
import ModalField from '~/components/modals/shared/ModalField.vue';
import RetryButton from '~/components/shared/RetryButton.vue';
import { useContactSupport } from '~/composables/useContactSupport';
import { DEPENDENTS_READING, useGiftDependents } from '~/composables/useGiftDependents';
import { authService } from '~/services/auth-service';
import { useAuthStore } from '~/stores/auth';
import { memberFacingMessage } from '~/utils/errors';

const emit = defineEmits<{
    closeDialog: [];
}>();

const auth = useAuthStore();
const { showSuccess, showError } = useGlobalToast();
const { sponsorsDependents, dependentsOn } = useGiftDependents();
const contactSupport = useContactSupport();

/** The member's own record, taken as the modal opens, since that is the account the member decides on. */
const person = usePersonStore().person;

/**
 * The member owns a license they bought, so app-api won't delete the account: the modal offers
 * support instead, and neither reads who depends on the plan nor asks for the deletion.
 */
const ownsPurchasedLicense = person?.owns_purchased_license === true;

/**
 * The seat the member's gifts ride on: the one they hold, when it is a live Pro or Signature seat
 * (`sponsorsDependents`), on an account the member can delete.
 */
const held = person?.license;
const giftSeat = !ownsPurchasedLicense && held && sponsorsDependents(held) ? held : null;

/** The people whose protection deleting the account ends, in the order they accepted. */
const dependents = ref<GiftDependent[]>([]);
const readingDependents = ref(giftSeat !== null);
/** What the status region says about the read: set by each read, never as the region mounts. */
const readingStatus = ref('');
/** The last read of who depends on the plan failed, and no read since has landed. */
const dependentsUnread = ref(false);

/** Delete account waits until the people are named, or the second failed read gives up on the names. */
const dependentsSettled = computed(() => !readingDependents.value && !dependentsUnread.value);

async function readDependents(): Promise<void> {
    if (!giftSeat) return;

    readingDependents.value = true;
    readingStatus.value = DEPENDENTS_READING;
    const read = await dependentsOn(giftSeat);
    readingDependents.value = false;
    readingStatus.value = '';
    dependentsUnread.value = read === null;
    dependents.value = read ?? [];
}

onMounted(() => {
    void readDependents();
});

const root = ref<HTMLElement | null>(null);
const peopleTitle = ref<HTMLElement | null>(null);

/** Where focus goes when a read lands and takes Try again: the question naming the people, or the confirmation. */
const nextStep = () => peopleTitle.value ?? root.value?.querySelector<HTMLInputElement>('#delete-confirmation');

const deleteConfirmation = ref('');
const isDeleting = ref(false);

/**
 * Delete the account once the member has typed DELETE, then sign them out. A refusal leaves the
 * modal as it was, with the API's own sentence when it gave one, which is how a license bought
 * since the modal opened is said. A deletion that went through keeps the button busy while the
 * sign-out leaves the app, so nothing can ask twice.
 */
async function confirmDelete(): Promise<void> {
    if (deleteConfirmation.value !== 'DELETE' || isDeleting.value) return;

    isDeleting.value = true;
    try {
        await authService.deleteAccount();
    } catch (error: unknown) {
        showError("We couldn't delete your account", memberFacingMessage(error, 'Try again in a few minutes.'));
        isDeleting.value = false;
        return;
    }

    showSuccess('Account deleted', 'Your account and its data are gone.');
    await auth.logout();
    await navigateTo('/auth/login');
}

/**
 * Support closes an account the member can't delete. The modal closes before the chat opens: while
 * open it keeps Tab inside itself, and closing after the chat opened would take focus back out of
 * the chat to the control that opened the modal.
 */
function closeForSupport(): void {
    emit('closeDialog');
    void contactSupport();
}
</script>

<style lang="scss" scoped>
.delete-warning {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
}

.delete-warning__title {
    margin: 0;
    color: var(--ink);
    font-size: var(--text-base);
    font-weight: var(--weight-semibold);
    line-height: var(--leading-base);
}

.delete-refusal,
.delete-warning__text,
.delete-warning__list {
    margin: 0;
    color: var(--ink);
    font-size: var(--text-sm);
    line-height: var(--leading-sm);
}

.delete-warning__text--strong {
    font-weight: var(--weight-semibold);
}

.delete-warning__list {
    padding-left: var(--space-5);
    list-style: disc;
}

// On the page's own ground: the people are named as a question for the member to answer, not as
// an alarm (DESIGN.md: There is no red ground).
.delete-dependents {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
}

// The way to read again sits under the sentence that says the read failed, at its own width.
.delete-dependents--unread {
    align-items: flex-start;
}

.delete-dependents__title {
    margin: 0;
    color: var(--ink);
    font-size: var(--text-sm);
    font-weight: var(--weight-semibold);
    line-height: var(--leading-sm);
}

.delete-dependents__loss {
    margin: 0;
}
</style>
